import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { useCustomerPublicGetCustomerRatings } from '@eneco/dc/hooks';
import { Box, RatingStar, Stack, Text, TextLink } from '@eneco/sparky';

import type { CustomerRatingContent } from './customer-rating-content.types';

/**
 * `CustomerRating` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane (`libs/sitecore/components/content/src/CustomerRating/
 * CustomerRating.tsx`, itself a thin wrapper over the shared `@eneco/components` `RatingSummary`):
 * a star rating plus a review-count link, sourced either from CMS-authored text (`ratingNumberText`/
 * `numberOfReviewsText`) or a live DC lookup (`useCustomerPublicGetCustomerRatings`), switched by
 * `getLiveRatingDataOption`. Renders nothing when neither source yields a number.
 *
 * Uses `adapter.toLink` + a plain Sparky `TextLink`, not `RatingSummary`'s own `useLinkComponent`
 * (`@eneco/link`'s JSS-shaped `LinkFieldValue`) — the same substitution every other migrated block in
 * this lane makes, since `adapter.toLink` already normalizes both providers' link shapes.
 */
const CustomerRating: FC = () => {
  const { adapter } = useCms();
  const content = useContent<CustomerRatingContent>();
  const { data } = useCustomerPublicGetCustomerRatings();

  const useLiveData = content.getLiveRatingDataOption;
  const rating = useLiveData ? data?.averageScore : Number(content.ratingNumberText);
  const ratingAmount = useLiveData ? data?.ratingsAmount : Number(content.numberOfReviewsText);

  if (typeof rating !== 'number' || Number.isNaN(rating)) return null;

  const link = adapter.toLink(content.link);
  const fallbackText = content.text ?? '';
  const reviewText = content.combineLiveDataWithTextOption ? `${ratingAmount} ${link?.text ?? fallbackText}` : (link?.text ?? fallbackText);

  return (
    <Box aria-label={reviewText}>
      <Stack gap="2" alignY="start" direction="row">
        <Text size="BodyS" weight="bold" as="span">
          {`${rating.toFixed(1)}/10`}
        </Text>
        <RatingStar maxStars={5} score={rating} />
        <Text size="BodyS">
          {link?.href ? (
            <TextLink href={link.href} target={link.target} emphasis="high">
              {reviewText}
            </TextLink>
          ) : (
            reviewText
          )}
        </Text>
      </Stack>
    </Box>
  );
};

export default CustomerRating;
